import moment from 'moment';

import { ApiService }                 from '../../app-lite/api/api.service';
import { CodeTypeModel }              from '../../app-lite/core/models/code-type';
import { CompanyCounterSettings }     from '../../app-lite/companies/models/company.counter-settings';
import { CompanyIntegrationsModel }   from '../../app-lite/companies/models/company.integrations';
import { CompanyInvoiceSettings }     from '../../app-lite/companies/models/company.invoice-settings';
import { CompanyService }             from '../../app-lite/companies/company.service';
import { CompanySettingsModel }       from '../../app-lite/companies/models/company.settings';
import { CompanyVariableLabelsModel } from '../../app-lite/companies/models/company.variable-labels';
import { ConfirmService }             from '../../app-lite/messages/confirm/confirm.service';
import { JsonService }                from '../../app-lite/core/services/json.service';
import { SessionService }             from '../../app-lite/sessions/session.service';
import { TaxGroupModel }              from '../../app-lite/companies/models/tax-group';
import { UtcOffset }                  from '../../app-lite/companies/models/utc-offset';

import ProductSearchResultsHtml from '../../app-lite/products/templates/product.search.results.html';
import RoundingWarningHtml      from '../../app-lite/core/templates/rounding-warning.html';

interface CompanySettingsScope extends ng.IScope {
  block                       : ng.blockUI.BlockUIService;
  bundledPrintStyles          : Array<CodeTypeModel>;
  company                     : any;
  companyApi                  : any;
  companyInvoiceSettings      : CompanyInvoiceSettings;
  companySettings             : CompanySettingsModel;
  contact_method_types        : Array<CodeTypeModel>;
  counters                    : CompanyCounterSettings;
  Dealer                      : any;
  grantDealerAccess           : () => void;
  init                        : () => void;
  integrations                : CompanyIntegrationsModel;
  multibranchSsoWarning       : string;
  offsets                     : Array<UtcOffset>;
  paymentTerms                : Array<CodeTypeModel>;
  product_cost_types          : Array<CodeTypeModel>;
  productSearch               : any;
  report_formats              : Array<CodeTypeModel>;
  reportIsAutocrew            : () => boolean;
  reportIsBosch               : () => boolean;
  reportIsDefault             : () => boolean;
  reportIsLabour              : () => boolean;
  reportIsLetterhead          : () => boolean;
  reportIsStretched           : () => boolean;
  reservedStock               : () => void;
  revokeDealerAccess          : () => void;
  roundWarning                : () => void;
  save                        : ( form : ng.IFormController ) => void;
  selectTime                  : ( event : Event, property : string ) => void;
  session                     : any;
  sessionApi                  : SessionService;
  shop_closes                 : string;
  shop_opens                  : string;
  taxGroups                   : Array<TaxGroupModel>;
  toggleMultiTax              : ( field_name : string, people_name : string, old_value : any, new_value : any ) => void;
  twoFactorAuthExpirationTypes: Array<CodeTypeModel>;
  updateUtcSettings           : () => void;
  validateVehicleGroups       : ( evt : Event, params : any ) => void;
  variableLabels              : CompanyVariableLabelsModel;
  vehicle_body_groups         : Array<CodeTypeModel>;
}

export function CompanySettingsCtrl (
  $q                          : ng.IQService,
  $rootScope                  : any,
  $sce                        : ng.ISCEService,
  $scope                      : CompanySettingsScope,
  $state                      : ng.ui.IStateService,
  $translate                  : ng.translate.ITranslateService,
  $uibModal                   : ng.ui.bootstrap.IModalService,
  blockUI                     : ng.blockUI.BlockUIService,
  company                     : any,
  companyApi                  : CompanyService,
  companyInvoiceSettings      : CompanyInvoiceSettings,
  companySettings             : CompanySettingsModel,
  companyVariableLabels       : CompanyVariableLabelsModel,
  confirm                     : ConfirmService,
  counters                    : CompanyCounterSettings,
  Dealer                      : any,
  integrations                : CompanyIntegrationsModel,
  jsonApi                     : JsonService,
  messages                    : any,
  productCostTypes            : Array<CodeTypeModel>,
  sessionObjAPI               : SessionService,
  taxGroups                   : Array<TaxGroupModel>,
  twoFactorAuthExpirationTypes: Array<CodeTypeModel>,
  workshopAPI                 : ApiService
) {

  $scope.block                        = blockUI.instances.get('companySettingsBlock');
  $scope.company                      = company;
  $scope.companyApi                   = companyApi;
  $scope.companyInvoiceSettings       = companyInvoiceSettings;
  $scope.companySettings              = companySettings;
  $scope.counters                     = counters;
  $scope.integrations                 = integrations;
  $scope.multibranchSsoWarning        = $sce.trustAsHtml($translate.instant('COMPANY_SPACE.SETTINGS.MULTIBRANCH_SSO_WARNING'));
  $scope.product_cost_types           = productCostTypes;
  $scope.session                      = sessionObjAPI.base();
  $scope.sessionApi                   = sessionObjAPI;
  $scope.shop_closes                  = moment($scope.company.shop_closes, 'HH:mm:ss').format('YYYY-MM-DDTHH:mm:ss');
  $scope.shop_opens                   = moment($scope.company.shop_opens, 'HH:mm:ss').format('YYYY-MM-DDTHH:mm:ss');
  $scope.taxGroups                    = taxGroups;
  $scope.twoFactorAuthExpirationTypes = twoFactorAuthExpirationTypes;
  $scope.variableLabels               = companyVariableLabels;

  $scope.Dealer   = new Dealer($scope.company.dealer_id);

  // This field is an array, but stored as a string in the database. So we must parse it
  $scope.company.vehicle_body_group = jsonApi.parse($scope.company.vehicle_body_group);

  $scope.productSearch = {
    value: {
      id       : $scope.companySettings.default_labour_product_id,
      item_code: $scope.companySettings.default_labour_product_name
    },
    templateUrl: ProductSearchResultsHtml,
    format     : ( product : any ) => angular.isObject(product) ? product.item_code: product,
    query      : ( value : string ) => workshopAPI.get('/products/product_labor_list', [ value, 0, 5 ]),
    select ( product : any ) {
      $scope.companySettings.default_labour_product_id   = product.id;
      $scope.companySettings.default_labour_product_name = product.item_code;
    }
  };

  $scope.grantDealerAccess = function () {
    $scope.Dealer.grantAccess()
    .then(() => {
      messages.show($translate.instant('JS_SPACE.DEALER.ACCESS_GRANTED'), 'success');

      $state.reload();
    })
    .catch(err => {
      messages.error(err);
    });
  };

  $scope.init = function () {
    $scope.block.start();

    $q.all({
      bundledPrintStyles  : workshopAPI.get('system/bundled_item_print_types'),
      contact_method_types: workshopAPI.get('/system/preferred_contact_method_types/', null, 'contactMethods'),
      offsets             : workshopAPI.get('/system/utc_offset_types/', null, 'utcOffsets'),
      paymentTerms        : workshopAPI.get('system/payment_term_types'),
      report_formats      : workshopAPI.get('/system/report_format_types/', null, 'reportFormats'),
      vehicle_body_groups : workshopAPI.get('/vehicle_body_group_types', [
        0, '*', 'CODE', 'asc'
      ])
    })
    .then(( options : any ) => {
      for (const key in options) {
        $scope[key] = options[key];
      }
    })
    .finally(() => {
      $scope.block.stop();
    });
  };

  $scope.reservedStock = function () {
    if ($scope.company.reserved_stock) {
      confirm.generic($translate.instant('JS_SPACE.CONFIRM.RESERVED_STOCK_ON'))
      .then(() => workshopAPI.post('/products/calculate_reserved_stock_levels'))
      .then (() => {
        messages.show($translate.instant('JS_SPACE.MESSAGES.RESERVED_STOCK_ON'), 'success');
      })
      .catch(err => {
        messages.error(err);
      });
    }
    else {
      confirm.generic($translate.instant('JS_SPACE.CONFIRM.RESERVED_STOCK_OFF'))
      .then(() => workshopAPI.post('/products/clear_reserved_stock'))
      .then(() => {
        messages.show($translate.instant('JS_SPACE.MESSAGES.RESERVED_STOCK_OFF'), 'success');
      })
      .catch(err => {
        messages.error(err);
      });
    };
  };

  $scope.reportIsAutocrew = function () {
    return $scope.company.report_format === 'AUTOCREW';
  };

  $scope.reportIsBosch = function () {
    return $scope.company.report_format === 'BOSCH';
  };

  $scope.reportIsDefault = function () {
    return $scope.company.report_format === 'DEFAULT';
  };

  $scope.reportIsLabour = function () {
    return $scope.company.report_format === 'LABOUR';
  };

  $scope.reportIsLetterhead = function () {
    return $scope.company.report_format === 'LETTERHEAD' || $scope.company.report_format === 'LETTERHEAD2';
  };

  $scope.reportIsStretched = function () {
    return $scope.company.report_format === 'STRETCHED';
  };

  $scope.revokeDealerAccess = function() {
    $scope.Dealer.revokeAccess()
    .then(() => {
      messages.show($translate.instant('JS_SPACE.DEALER.ACCESS_REVOKED'), 'success');

      $state.reload();
    })
    .catch(err => {
      messages.error(err);
    });
  };

  $scope.roundWarning = function () {
    $uibModal.open({ templateUrl: RoundingWarningHtml });
  };

  $scope.save = function ( form : ng.IFormController ) {
    const warning = form.round_total.$dirty && $scope.company.round_total;

    $scope.block.start();

    if ($scope.company.tax_group_id) {
      $scope.company.tax_group_name = $scope.taxGroups
      .filter(group => group.id === $scope.company.tax_group_id)
      [0].name;
    }

    // Some records already saved in the database
    // include a UTC offset, so we need to make
    // sure that is removed.
    $scope.company.shop_opens  = moment($scope.shop_opens).format('HH:mm:ss');
    $scope.company.shop_closes = moment($scope.shop_closes).format('HH:mm:ss');

    /**
     * REFERENCE: WOR-4280
     *
     * we want to start saving company_settings items on the company_settings table
     * directly. Here, the company_settings object that exists on the company
     * object is overriding the company_settings. So, set them to be equal before
     * saving.
     */
    $scope.company.company_settings = $scope.companySettings;

    workshopAPI.patch('/company_settings', $scope.companySettings, 'company_setting')
    .then(() => workshopAPI.patch('/company_counter_settings', $scope.counters, 'company_counter_setting'))
    .then(() => workshopAPI.patch('company_invoice_settings', $scope.companyInvoiceSettings, 'company_invoice_setting'))
    .then(() => workshopAPI.patch('company_integrations', $scope.integrations, 'company_integration'))
    .then(() => workshopAPI.patch('/company_variable_field_labels', $scope.variableLabels, 'company_variable_field_label'))
    .then(() => workshopAPI.patch('/company', $scope.company, 'company'))
    .then(( company : any ) => {
      messages.show($translate.instant('JS_SPACE.MESSAGES.SAVED.SETTINGS'), 'success');

      form.$setPristine();

      $state.reload();

      if (warning && ($rootScope.Company.hasIntegration('xero') || $rootScope.Company.hasIntegration('myob'))) {
        $scope.roundWarning();
      }
    })
    .catch(err => {
      messages.error(err);
    })
    .finally(() => {
      $scope.block.stop();
    });
  };

  $scope.selectTime = function ( event : Event, property : string ) {
    const picker = $(event.currentTarget)
    .datetimepicker({
      initialDate : new Date(),
      showMeridian: true,
      startView   : 'day'
    })
    .on('changeDate', ( ev : any ) => {
      let date : Date | string = new Date(ev.date.valueOf());

      date = new Date(
        date.getUTCFullYear(),
        date.getUTCMonth(),
        date.getUTCDate(),
        date.getUTCHours(),
        date.getUTCMinutes()
      ).toISOString();

      $scope[property] = date;
      $scope.company[property] = moment(date).format('HH:mm:ss');

      if (!$scope.$$phase) {
        $scope.$apply();
      }

      $(picker).datetimepicker('hide');
    });

    $(picker).datetimepicker('show');
  };

  $scope.toggleMultiTax = function () {
    $scope.company.use_multi_tax = $scope.company.price_includes_tax ? false: $scope.company.use_multi_tax;
  };

  $scope.updateUtcSettings = function () {
    $scope.company.utc_offset = $rootScope.Company.getUtcOffsetFromCode($scope.companySettings.utc_description, $scope.offsets);
  };

  $scope.validateVehicleGroups = function ( evt : Event, params : any ) {
    if ( params.deselected ) {
      const data = {
        current_groups  : angular.copy($scope.company.vehicle_body_group),
        group_to_destroy: params.deselected.split(':')[ 1 ] || params.deselected
      };

      data.current_groups.push(data.group_to_destroy);

      workshopAPI.post('/company/can_destroy_vehicle_body_group', data)
      .then(( isDeletable : boolean ) => {
        if ( !isDeletable ) {
          $scope.company.vehicle_body_group.push(data.group_to_destroy);

          messages.show($translate.instant('JS_SPACE.MESSAGES.VEHICLES_USING_BODY_TYPE'), 'error');
        }
      })
      .catch(err => {
        messages.error(err);
      });
    }
  };

  $scope.init();
};
